import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinusSquare } from '@fortawesome/free-regular-svg-icons';
import { faChalkboardTeacher, faChartColumn, faChartDiagram, faDiagramPredecessor, faGauge, faHurricane, faKeyboard, faLandmark, faLayerGroup, faNetworkWired, faPlusSquare, faScrewdriverWrench, faSlidersH, faStaffSnake, faUsersRectangle, faVault, faWindowRestore, faSun, faWalkieTalkie, faFileShield, faCodeCompare, faChartGantt, faGears, faVcard } from '@fortawesome/free-solid-svg-icons';

const SidebarContainer = styled(motion.div)`
  width: ${props => props.collapsed ? '70px' : '280px'};
  height: 100vh;
  background: #ffffff;
  border-right: 1px solid var(--border-color);
  padding: .2rem .1rem ${props => props.collapsed ? '0.5rem' : '1rem'};
  display: flex;
  flex-direction: column;
  position: relative;
  overflow-y: auto;
  overflow-x:hidden;
  z-index: 10;
  box-shadow: 2px 0 5px rgba(0, 102, 255, 0.03);
  transition: width 0.3s ease;
`;

const Logo = styled.img`
  height: ${props => props.collapsed ? '30px' : '60px'};
  margin-bottom: 2rem;
  display: block;
  margin-left: auto;
  margin-right: auto;
`;

const NavGroup = styled.div`
  margin-bottom: 1rem;
`;

const NavGroupTitle = styled.div`
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--text-secondary);
  margin-bottom: 0.8rem;
  padding-left: 0.1rem;
  font-weight: 600;
  opacity: 0.7;
  display: ${props => props.collapsed ? 'none' : 'block'};
`;

const NavItem = styled(motion.div)`
  padding: 0.6rem ${props => props.collapsed ? '0' : '0.8rem'};
  border-radius: 8px;
  margin-bottom: 0.3rem;
  display: flex;
  align-items: center;
  justify-content: ${props => props.collapsed ? 'center' : 'flex-start'};
  color: ${props => props.active ? '#0066FF' : 'var(--text-primary)'};
  background: ${props => props.active ? 'rgba(0, 102, 255, 0.05)' : 'transparent'};
  border-left: ${props => !props.collapsed && props.active ? '3px solid #0066FF' : '3px solid transparent'};
  font-weight: ${props => props.active ? '500' : '400'};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(0, 102, 255, 0.03);
    color: #0066FF;
  }
`;

const SubNavItem = styled(NavItem)`
  overflow-x:hidden;
  padding-left: ${props => props.collapsed ? '0' : '.85rem'};
  font-size: 0.85rem;
  margin-bottom: 0.2rem;
`;

const NavIcon = styled.div`
  width: 20px;
  height: 20px;
  margin-right: ${props => props.collapsed ? '0' : '12px'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  color: ${props => props.active ? '#0066FF' : 'var(--text-secondary)'};
`;

const NavText = styled.div`
  font-size: 0.9rem;
  display: ${props => props.collapsed ? 'none' : 'block'};
`;

const GroupToggle = styled.div`
  margin-left: auto;
  font-size: 0.8rem;
  color: var(--text-secondary);
  cursor: pointer;
  transition: transform 0.3s ease;
  transform: ${props => props.expanded ? 'rotate(90deg)' : 'rotate(0deg)'};
  display: ${props => props.collapsed ? 'none' : 'block'};
`;

const Sidebar = ({ collapsed = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [hoveredItem, setHoveredItem] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({
    dashboard: true,
    agentWorkspace: false,
    agentEcosystem: false,
    knowledge: true,
    governance: false,
    system: false
  });
  
  const toggleGroup = (group) => {
    setExpandedGroups({
      ...expandedGroups,
      [group]: !expandedGroups[group]
    });
  };
  
  const isActive = (path) => {
    return location.pathname === path;
  };
  
  const mainNavItems = [
    { id: 'dashboard', icon: faGauge, label: 'Agentic Dashboard', path: '/dashboard' },
    { id: 'mission-control', icon: faSlidersH, label: 'Mission Control', path: '/mission-control' },
  ];
  
  const agentWorkspaceItems = [
    { id: 'new-business-hub', icon: faHurricane, label: 'New Business', path: '/workstream-hub/New Business' },
    { id: 'claims-hub', icon: faStaffSnake, label: 'Claims', path: '/workstream-hub/Claims Processing' },
    { id: 'services', icon: faVcard, label: 'Servicing', path: '/workstream-hub/Servicing' },
    // { id: 'document-queue', icon: faLayerGroup, label: 'Document', path: '/document-queue' },
    { id: 'fraud-vault', icon: faVault, label: 'FWA', path: '/workstream-hub/Fraud waste and abuse' },
  ];
  
  const agentEcosystemItems = [
    { id: 'agent-studio', icon: faChalkboardTeacher, label: 'Agent Studio', path: '/agent-studio' },
    { id: 'personas', icon: faUsersRectangle, label: 'Personas', path: '/personas' },
    { id: 'model-hub', icon: faChartDiagram, label: 'Model Hub', path: '/model-hub' },
    { id: 'tools', icon: faScrewdriverWrench, label: 'Tools', path: '/tools' },
    { id: 'performance', icon: faChartColumn, label: 'Performance', path: '/performance' },
  ];
  
  const knowledgeItems = [
    { id: 'knowledge-base', icon: faLandmark, label: 'Knowledge Base', path: '/knowledge-base' },
    { id: 'entity', icon: faWindowRestore, label: 'Entity', path: '/entity' },
    { id: 'data-sources', icon: faDiagramPredecessor, label: 'Data Sources', path: '/data-sources' },
    { id: 'prompt-library', icon: faKeyboard, label: 'Prompt Library', path: '/prompt-library' },
    { id: 'agent-pipelines', icon: faNetworkWired, label: 'Agent Pipelines', path: '/agent-pipelines' },
  ];
  
  const governanceItems = [
    { id: 'security-governance', icon: faSun, label: 'Security & Governance', path: '/security-governance' },
    { id: 'observability', icon: faWalkieTalkie, label: 'Observability', path: '/observability' },
    { id: 'policies', icon: faFileShield, label: 'Policies', path: '/policies' },
    { id: 'feedback-loops', icon: faCodeCompare, label: 'Feedback Loops', path: '/feedback-loops' },
  ];
  
  const systemItems = [
    { id: 'analytics', icon: faChartGantt, label: 'Analytics', path: '/analytics' },
    { id: 'settings', icon: faGears, label: 'Settings', path: '/settings' },
  ];
  
  const renderNavItems = (items, isSubMenu = false) => {
    return items.map(item => (
      <Link to={item.path} key={item.id} style={{ textDecoration: 'none' }}>
        {isSubMenu ? (
          <SubNavItem
            active={isActive(item.path)}
            onMouseEnter={() => setHoveredItem(item.id)}
            onMouseLeave={() => setHoveredItem(null)}
            whileHover={{ x: collapsed ? 0 : 5 }}
            transition={{ type: 'spring', stiffness: 300 }}
            collapsed={collapsed}
          >
            <NavIcon active={isActive(item.path)} collapsed={collapsed}>
            {typeof item.icon === 'string' ? item.icon : <FontAwesomeIcon icon={item.icon} />}
            </NavIcon>
            <NavText collapsed={collapsed}>{item.label}</NavText>
          </SubNavItem>
        ) : (
          <NavItem
            active={isActive(item.path)}
            onMouseEnter={() => setHoveredItem(item.id)}
            onMouseLeave={() => setHoveredItem(null)}
            whileHover={{ x: collapsed ? 0 : 5 }}
            transition={{ type: 'spring', stiffness: 300 }}
            collapsed={collapsed}
          >
            {/* <NavIcon active={isActive(item.path)} collapsed={collapsed}><FontAwesomeIcon icon={item.icon} /></NavIcon> */}
            <NavIcon active={isActive(item.path)} collapsed={collapsed}>
              {typeof item.icon === 'string' ? item.icon : <FontAwesomeIcon icon={item.icon} />}
            </NavIcon>
            <NavText collapsed={collapsed}>{item.label}</NavText>
          </NavItem>
        )}
      </Link>
    ));
  };
  
  const renderGroupWithToggle = (title, items, groupKey) => {
    return (
      <NavGroup>
        <NavItem onClick={() => toggleGroup(groupKey)} collapsed={collapsed} style={{justifyContent:"space-between"}}>
          <NavText collapsed={collapsed} style={{ color:"#9c9c9c"}}>{title}</NavText>
          <NavIcon collapsed={collapsed}>{expandedGroups[groupKey] ? (<FontAwesomeIcon icon={faMinusSquare} />) : (<FontAwesomeIcon icon={faPlusSquare} />)}</NavIcon>
          {/* <GroupToggle expanded={expandedGroups[groupKey]} collapsed={collapsed} /> */}
        </NavItem>
        {expandedGroups[groupKey] && renderNavItems(items, true)}
      </NavGroup>
    );
  };
  
  return (
    <SidebarContainer collapsed={collapsed}>
      <Logo 
        src={collapsed ? '/images/logo/meclaims-icon.png' : '/images/logo/bmm.png'} 
        alt="Logo" 
        collapsed={collapsed}
      />
      
      <NavGroup>
        {renderNavItems(mainNavItems)}
      </NavGroup>
      
      {renderGroupWithToggle('Work Stream', agentWorkspaceItems, 'agentWorkspace')}
      {renderGroupWithToggle('Agent Ecosystem', agentEcosystemItems, 'agentEcosystem')}
      {renderGroupWithToggle('Knowledge & Flows', knowledgeItems, 'knowledge')}
      {renderGroupWithToggle('Governance', governanceItems, 'governance')}
      {renderGroupWithToggle('System', systemItems, 'system')}
    </SidebarContainer>
  );
};

export default Sidebar;
